import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import logo from "../../assets/images/logo.svg";
import logoDark from "../../assets/images/logoDark.svg";
import { HiOutlineMenuAlt3, HiX } from "react-icons/hi";
import Popup from "reactjs-popup";
import ContactUs from "../../pages/ContactUs";

const Navbar = ({ darkMode = false }) => {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showSolutions, setShowSolutions] = useState(false);
  const [contact, setContact] = useState(false);

  const scrollHandler = useCallback(() => {
    const isScrolled = window.pageYOffset > 50;
    setScrolled(isScrolled);
  }, []);

  useEffect(() => {
    scrollHandler();
    window.addEventListener("scroll", scrollHandler);
    return () => window.removeEventListener("scroll", scrollHandler);
  }, [scrollHandler]);

  // Use white text/logo when on dark background (darkMode and not scrolled)
  const useWhiteText = darkMode && !scrolled;
  
  // Explicit colors for consistent rendering
  const textColor = useWhiteText ? '#ffffff' : '#1e293b';
  const textColorSecondary = useWhiteText ? 'rgba(255,255,255,0.7)' : '#64748b';

  const navLinks = [
    {
      label: "Solutions",
      hasDropdown: true,
      children: [
        { label: "R&D", route: "/rnd" },
        { label: "Medical Affairs", route: "/medicalAffairs" },
      ],
    },
    { label: "About", route: "/about" },
    { label: "Research", route: "/research" },
    { label: "Contact", onClick: () => setContact(true) },
  ];

  return (
    <header
      className={`fixed w-full z-50 transition-all duration-300 ${
        useWhiteText
          ? "bg-black/20 backdrop-blur-sm"
          : "bg-white shadow-sm"
      }`}
    >
      <Popup
        onClose={() => setContact(false)}
        modal
        open={contact}
        className="rounded-xl"
        position="center center"
      >
        <ContactUs closeContact={() => setContact(false)} />
      </Popup>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo - use white logo on dark background */}
          <div
            onClick={() => navigate("/")}
            className="flex-shrink-0 cursor-pointer flex items-center gap-4"
          >
            <img
              src={useWhiteText ? logoDark : logo}
              alt="Clinica AI"
              className="h-10 md:h-12"
            />
            <span 
              className="hidden lg:block text-base font-medium"
              style={{ color: textColorSecondary }}
            >
              AI & RWD for better patient care
            </span>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-2">
            {navLinks.map((link, index) => (
              <div key={index} className="relative">
                {link.hasDropdown ? (
                  <div
                    className="relative"
                    onMouseEnter={() => setShowSolutions(true)}
                    onMouseLeave={() => setShowSolutions(false)}
                  >
                    <button
                      className="px-5 py-2.5 text-base font-medium rounded-lg transition-colors"
                      style={{ 
                        color: textColor,
                        backgroundColor: 'transparent'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = useWhiteText ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      {link.label}
                    </button>
                    {showSolutions && (
                      <>
                        {/* Invisible bridge to prevent gap */}
                        <div className="absolute top-full left-0 w-full h-2" />
                        <div 
                          className="absolute top-full left-0 mt-2 w-52 rounded-lg shadow-lg py-2"
                          style={{ backgroundColor: '#ffffff', border: '1px solid #e2e8f0' }}
                        >
                          {link.children.map((child, idx) => (
                            <button
                              key={idx}
                              onClick={() => {
                                navigate(child.route);
                                setShowSolutions(false);
                              }}
                              className="w-full text-left px-5 py-2.5 text-base transition-colors"
                              style={{ color: '#1e293b' }}
                              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f1f5f9'}
                              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                            >
                              {child.label}
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                ) : (
                  <button
                    onClick={link.onClick || (() => navigate(link.route))}
                    className="px-5 py-2.5 text-base font-medium rounded-lg transition-colors"
                    style={{ 
                      color: textColor,
                      backgroundColor: 'transparent'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = useWhiteText ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    {link.label}
                  </button>
                )}
              </div>
            ))}
            {/* Sign in button - always visible with contrasting colors */}
            <button
              onClick={() => navigate("/auth")}
              style={{
                backgroundColor: useWhiteText ? '#ffffff' : '#0f172a',
                color: useWhiteText ? '#0f172a' : '#ffffff',
              }}
              className="ml-4 px-6 py-2.5 text-base font-semibold rounded-lg transition-colors shadow-md hover:opacity-90"
            >
              Sign in
            </button>
          </nav>

          {/* Mobile menu button */}
          <button
            onClick={() => setShowMobileMenu(!showMobileMenu)}
            className="md:hidden p-2.5 rounded-lg"
            style={{ color: textColor }}
          >
            {showMobileMenu ? (
              <HiX className="w-7 h-7" />
            ) : (
              <HiOutlineMenuAlt3 className="w-7 h-7" />
            )}
          </button>
        </div>

        {/* Mobile Navigation */}
        {showMobileMenu && (
          <div 
            className="md:hidden py-5 rounded-b-lg animate-slide-in"
            style={{ backgroundColor: '#ffffff', borderTop: '1px solid #e2e8f0' }}
          >
            <div className="space-y-1">
              {navLinks.map((link, index) => (
                <div key={index}>
                  {link.hasDropdown ? (
                    <>
                      <div 
                        className="px-5 py-2.5 text-base font-medium"
                        style={{ color: '#64748b' }}
                      >
                        {link.label}
                      </div>
                      {link.children.map((child, idx) => (
                        <button
                          key={idx}
                          onClick={() => {
                            navigate(child.route);
                            setShowMobileMenu(false);
                          }}
                          className="w-full text-left px-8 py-2.5 text-base hover:bg-slate-100"
                          style={{ color: '#1e293b' }}
                        >
                          {child.label}
                        </button>
                      ))}
                    </>
                  ) : (
                    <button
                      onClick={() => {
                        if (link.onClick) {
                          link.onClick();
                        } else {
                          navigate(link.route);
                        }
                        setShowMobileMenu(false);
                      }}
                      className="w-full text-left px-5 py-2.5 text-base font-medium hover:bg-slate-100 rounded-lg"
                      style={{ color: '#1e293b' }}
                    >
                      {link.label}
                    </button>
                  )}
                </div>
              ))}
              <div className="pt-5 px-5">
                <button
                  onClick={() => {
                    navigate("/auth");
                    setShowMobileMenu(false);
                  }}
                  className="w-full py-3 text-base font-semibold rounded-lg transition-colors"
                  style={{ backgroundColor: '#0f172a', color: '#ffffff' }}
                >
                  Sign in
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Navbar;
